import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Card } from 'primereact/card';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Chart } from 'primereact/chart';
import { Dialog } from 'primereact/dialog';
import { handleGetRequest } from '../../service/GetTemplate';
import { endPoints } from '../../Config';
import { formatLocalizedDateTime, formatTimeFromMinutes } from '../../utilities/formatTime';
import { exportToExcel } from '../../utilities/exportToExcel';
import usePreferredLanguage from '../../utilities/usePreferredLanguage';
import { translateUiText } from '../../utilities/siteTranslations';

const getMonthBounds = (value) => {
    const base = value ? new Date(value) : new Date();
    const start = new Date(base.getFullYear(), base.getMonth(), 1);
    const end = new Date(base.getFullYear(), base.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start, end };
};

const getDayBounds = (startValue, endValue) => {
    const firstBase = startValue ? new Date(startValue) : new Date();
    const secondBase = endValue ? new Date(endValue) : firstBase;
    const first = new Date(firstBase.getFullYear(), firstBase.getMonth(), firstBase.getDate());
    const second = new Date(secondBase.getFullYear(), secondBase.getMonth(), secondBase.getDate());
    const minDate = first <= second ? first : second;
    const maxDate = first <= second ? second : first;
    const start = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
    const end = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate(), 23, 59, 59, 999);
    return { start, end };
};

const formatDateTime = (value, language = 'es') => formatLocalizedDateTime(value, language);

const formatDateOnly = (value, language = 'es') => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString(language === 'es' ? 'es-PA' : 'en-US');
};

const maskWhatsAppNumber = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '-';
    const digits = raw.replace(/\D/g, '');
    if (!digits) return raw;
    if (digits.length <= 4) return digits;
    return `***${digits.slice(-4)}`;
};

const getElapsedMinutes = (complaint) => {
    const createdAt = complaint?.createdAt ? new Date(complaint.createdAt) : null;
    if (!createdAt || Number.isNaN(createdAt.getTime())) return null;

    const endSource = complaint?.closedAt || complaint?.updatedAt || new Date();
    const endAt = new Date(endSource);
    if (Number.isNaN(endAt.getTime())) return null;

    return Math.max(0, Math.round((endAt.getTime() - createdAt.getTime()) / 60000));
};

const complaintElapsedBody = (rowData) => {
    const minutes = getElapsedMinutes(rowData);
    return minutes !== null ? formatTimeFromMinutes(minutes) : '-';
};

const isTaskMachineMatch = (task, selectedMachineIdSet, selectedNormalizedSerials, normalizeSerial) => {
    const machines = Array.isArray(task?.machines) ? task.machines : [];
    return machines.some((machine) => {
        const machineId = machine?._id || machine;
        if (selectedMachineIdSet.has(String(machineId))) return true;
        const normalizedSerial = normalizeSerial(machine?.serialNumber);
        return normalizedSerial && selectedNormalizedSerials.has(normalizedSerial);
    });
};

const getMaintenanceMachineLabel = (task, t = (value) => value) => {
    const machines = Array.isArray(task?.machines) ? task.machines : [];
    if (!machines.length) return '-';
    if (machines.length === 1) return normalizeMachineLabel(machines[0], t);
    return `${normalizeMachineLabel(machines[0], t)} +${machines.length - 1}`;
};

const normalizeMachineLabel = (machine, t = (value) => value) =>
    [
        machine?.machine || machine?.name || t('Unnamed Machine'),
        machine?.type ? t(String(machine.type).charAt(0).toUpperCase() + String(machine.type).slice(1).toLowerCase()) : t('Unknown Type'),
        machine?.location || t('No location'),
        machine?.serialNumber || machine?.deviceId || machine?.uuid || t('No ID')
    ].join(' - ');

const getQuestionLabel = (question) => {
    if (!question) return '-';
    return question?.text || question?.question || question?.name || question?.label || '-';
};

const getMeaningfulChatText = (complaint) => {
    if (!Array.isArray(complaint?.chat)) return '';

    const isMeaningfulText = (entry) => {
        const text = String(entry?.text || '').trim();
        if (!text || entry?.isImage) return false;
        if (/^\d{1,2}$/.test(text)) return false;
        return true;
    };

    const customerMessage = complaint.chat.find((entry) => {
        const from = String(entry?.from || '').toLowerCase();
        return isMeaningfulText(entry) && from !== 'panel' && from !== 'system';
    });
    if (customerMessage?.text) return customerMessage.text;

    const manualMessage = complaint.chat.find((entry) => {
        const from = String(entry?.from || '').toLowerCase();
        return isMeaningfulText(entry) && from !== 'system';
    });

    return manualMessage?.text || '';
};

const getUserQueryLabel = (complaint) => {
    const value = String(complaint?.userQuery || complaint?.query || complaint?.issue || getMeaningfulChatText(complaint) || '').trim();
    return value || '-';
};

const getLevelOneLabel = (complaint) => getQuestionLabel(complaint?.levelOneQuestion);
const getLevelTwoLabel = (complaint) => getQuestionLabel(complaint?.levelTwoQuestion);
const getLevelThreeLabel = (complaint) => getQuestionLabel(complaint?.levelThreeQuestion);
const getIssueSummary = (complaint) => {
    return (
        normalizeBreakdownLabel(complaint?.issueSummary) ||
        normalizeBreakdownLabel(getLevelThreeLabel(complaint)) ||
        normalizeBreakdownLabel(getLevelTwoLabel(complaint)) ||
        normalizeBreakdownLabel(getLevelOneLabel(complaint)) ||
        normalizeBreakdownLabel(getMeaningfulChatText(complaint)) ||
        normalizeBreakdownLabel(getUserQueryLabel(complaint)) ||
        '-'
    );
};

const getComplaintCustomerLabel = (complaint, machineById = new Map()) => {
    const machineId = complaint?.machine?._id || complaint?.machine;
    const machine = machineById.get(String(machineId)) || complaint?.machine || {};
    const customerName = [
        complaint?.customerName,
        complaint?.manualIntake?.customerName,
        complaint?.refund?.customerName,
        machine?.customerName,
        machine?.location
    ].find((value) => String(value || '').trim());

    return String(customerName || '').trim();
};

const normalizeBreakdownLabel = (value) => {
    const label = String(value || '').trim();
    return label && label !== '-' ? label : null;
};

const buildBreakdownRows = (rows, getLabel) => {
    const counts = new Map();
    rows.forEach((row) => {
        const label = normalizeBreakdownLabel(getLabel(row)) || '-';
        counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries())
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
};

const buildCombinedBreakdownRows = (rows, t = (value) => value) => {
    const counts = new Map();
    rows.forEach((row) => {
        const level1 = normalizeBreakdownLabel(getLevelOneLabel(row));
        const level2 = normalizeBreakdownLabel(getLevelTwoLabel(row));
        const level3 = normalizeBreakdownLabel(getLevelThreeLabel(row));
        const isManualTicket = !level1 && !level2 && !level3;
        const keyParts = isManualTicket
            ? [t('Manual Ticket'), t('Written Detail'), '-']
            : [level1 || '-', level2 || '-', level3 || '-'];
        const key = keyParts.join('||');
        const current = counts.get(key) || {
            level1: keyParts[0],
            level2: keyParts[1],
            level3: keyParts[2],
            count: 0
        };
        current.count += 1;
        counts.set(key, current);
    });

    return Array.from(counts.values()).sort(
        (a, b) =>
            b.count - a.count ||
            a.level1.localeCompare(b.level1) ||
            a.level2.localeCompare(b.level2) ||
            a.level3.localeCompare(b.level3)
    );
};

const truncateText = (value, maxLength = 140) => {
    const text = String(value || '').trim();
    if (!text) return '-';
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

const getComplaintOrigin = (complaint, t = (value) => value) => {
    const hasMenuSelection = !!(
        normalizeBreakdownLabel(getLevelOneLabel(complaint)) ||
        normalizeBreakdownLabel(getLevelTwoLabel(complaint)) ||
        normalizeBreakdownLabel(getLevelThreeLabel(complaint))
    );
    const hasReporter = !!complaint?.reportedBy;
    const hasPanelMessage = Array.isArray(complaint?.chat)
        ? complaint.chat.some((entry) => String(entry?.from || '').toLowerCase() === 'panel')
        : false;
    const text = `${getIssueSummary(complaint)} ${getMeaningfulChatText(complaint)}`.toLowerCase();

    if (hasMenuSelection) return t('WhatsApp Menu');
    if (text.includes('qr') || text.includes('repair')) return t('QR / Repair Flow');
    if (hasReporter || hasPanelMessage) return t('Manual Ticket / Panel');
    return t('Written Detail');
};

const PROBLEM_THEME_RULES = [
    { key: 'cash', label: 'Cash / Bill Acceptor', pattern: /(efectivo|cash|billetero|billete|bill|moneda|coin)/i },
    { key: 'card', label: 'Card / POS Payment', pattern: /(tarjeta|card|pos|punto|billetera|yappy|pago)/i },
    { key: 'product', label: 'Product Not Delivered', pattern: /(no entrega|no dio|no da|no despacho|producto|vend|drop|sensor)/i },
    { key: 'cleaning', label: 'Cleaning / Hygiene', pattern: /(limpieza|sucio|manchado|cucaracha|fumigaci[oó]n|aseo)/i },
    { key: 'display', label: 'Display / Screen', pattern: /(pantalla|display|touch|teclado|bot[oó]n)/i },
    { key: 'phone', label: 'Phone / Contact Sticker', pattern: /(tel[eé]fono|telefono|sticker|pegatina|contacto)/i },
    { key: 'light', label: 'Lighting', pattern: /(luz|luces|iluminaci[oó]n|bombillo)/i },
    { key: 'price', label: 'Pricing', pattern: /(precio|price|costo|valor)/i },
    { key: 'water', label: 'Water / Coffee System', pattern: /(agua|bomba|caf[eé]|vaso|az[uú]car|leche)/i }
];

const getProblemTheme = (complaint, t = (value) => value) => {
    const text = [
        getIssueSummary(complaint),
        getLevelThreeLabel(complaint),
        getLevelTwoLabel(complaint),
        getLevelOneLabel(complaint),
        getMeaningfulChatText(complaint)
    ].join(' ');
    const rule = PROBLEM_THEME_RULES.find((item) => item.pattern.test(text));
    return t(rule?.label || 'Other / Written Detail');
};

const getSlaBand = (complaint, t = (value) => value) => {
    const status = String(complaint?.status || '').toLowerCase();
    if (status !== 'close') return t('Still Open');
    const minutes = getElapsedMinutes(complaint);
    if (!Number.isFinite(minutes)) return t('Unknown');
    if (minutes < 24 * 60) return t('Closed under 24h');
    if (minutes < 48 * 60) return t('Closed 24-48h');
    return t('Closed 48h+');
};

const getMachineIdentity = (complaint, normalizeSerial) => {
    const machine = complaint?.machine || {};
    return (
        normalizeSerial(machine?.serialNumber) ||
        String(machine?._id || complaint?.machine || machine?.deviceId || machine?.uuid || '')
    );
};

const isRepeatComplaint = (complaint, allRows, normalizeSerial, windowDays = 30) => {
    const createdAt = complaint?.createdAt ? new Date(complaint.createdAt) : null;
    if (!createdAt || Number.isNaN(createdAt.getTime())) return false;
    const identity = getMachineIdentity(complaint, normalizeSerial);
    if (!identity) return false;
    const windowMs = windowDays * 24 * 60 * 60 * 1000;

    return allRows.some((row) => {
        if (String(row?._id) === String(complaint?._id)) return false;
        if (getMachineIdentity(row, normalizeSerial) !== identity) return false;
        const rowDate = row?.createdAt ? new Date(row.createdAt) : null;
        if (!rowDate || Number.isNaN(rowDate.getTime())) return false;
        return Math.abs(rowDate.getTime() - createdAt.getTime()) <= windowMs;
    });
};

const getRepairOrderCode = (order) => (order?._id ? `#${String(order._id).slice(-4).toUpperCase()}` : '-');

const getRepairOrderLink = (order) => (order?._id ? `/#/repair-orders/${order._id}` : null);

const getTechnicianName = (order) => `${order?.technician?.firstName || ''} ${order?.technician?.lastName || ''}`.trim() || '-';

const getRepairOrderElapsedMinutes = (order) => {
    if (Number.isFinite(order?.timeConsumedMinutes)) return order.timeConsumedMinutes;
    const createdAt = order?.createdAt ? new Date(order.createdAt) : null;
    const closedAt = order?.closedAt ? new Date(order.closedAt) : null;
    if (!createdAt || !closedAt || Number.isNaN(createdAt.getTime()) || Number.isNaN(closedAt.getTime())) return null;
    return Math.max(0, Math.round((closedAt.getTime() - createdAt.getTime()) / 60000));
};

const getRepairResult = (order, t = (value) => value) => {
    if (!order?.finalStatus) return '-';
    return t(order.finalStatus);
};

const getRepairOrderSummary = (orders, t = (value) => value) => {
    if (!orders.length) {
        return {
            code: '-',
            status: t('No Repair Order'),
            technician: '-',
            failureType: '-',
            failureCategory: '-',
            timeToClose: '-',
            actionsTaken: '-'
        };
    }

    const order = [...orders].sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0))[0];
    return {
        code: getRepairOrderCode(order),
        status: t(order?.status || '-'),
        technician: getTechnicianName(order),
        failureType: order?.typeOfFailure || '-',
        failureCategory: order?.failureCategory || '-',
        timeToClose: Number.isFinite(order?.timeConsumedMinutes) ? formatTimeFromMinutes(order.timeConsumedMinutes) : '-',
        actionsTaken: order?.actionsTaken || '-',
        link: getRepairOrderLink(order)
    };
};

const buildTopRows = (rows, limit = 8, otherLabel = 'Other') => {
    if (!rows.length) return [];
    if (rows.length <= limit) return rows;

    const topRows = rows.slice(0, limit);
    const remainingCount = rows.slice(limit).reduce((sum, row) => sum + row.count, 0);
    if (remainingCount > 0) {
        topRows.push({ label: otherLabel, count: remainingCount });
    }
    return topRows;
};

const buildChartOptions = (title, indexAxis = 'x') => ({
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false
        },
        title: {
            display: !!title,
            text: title
        }
    },
    indexAxis,
    scales:
        indexAxis === 'y'
            ? {
                  x: {
                      beginAtZero: true,
                      ticks: {
                          precision: 0
                      }
                  },
                  y: {
                      ticks: {
                          autoSkip: false
                      }
                  }
              }
            : {
                  y: {
                      beginAtZero: true,
                      ticks: {
                          precision: 0
                      }
                  }
              }
});

const buildDoughnutOptions = (title) => ({
    maintainAspectRatio: false,
    cutout: '58%',
    plugins: {
        legend: {
            position: 'top',
            labels: {
                usePointStyle: true
            }
        },
        title: {
            display: !!title,
            text: title
        }
    }
});

const doughnutValuePlugin = {
    id: 'doughnutValuePlugin',
    afterDatasetsDraw(chart) {
        const { ctx } = chart;
        const meta = chart.getDatasetMeta(0);
        const dataset = chart.data?.datasets?.[0]?.data || [];
        ctx.save();
        ctx.font = '600 12px sans-serif';
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        meta.data.forEach((element, index) => {
            const value = dataset[index];
            if (!value) return;
            const position = element.tooltipPosition();
            ctx.fillText(String(value), position.x, position.y);
        });

        ctx.restore();
    }
};

const complaintStatusBody = (rowData, t = (value) => value) => {
    const value = String(rowData?.status || '').toLowerCase();
    const labelMap = {
        open: t('Open'),
        in_progress: t('Finance'),
        awaiting_customer: t('Awaiting Customer'),
        close: t('Closed'),
        forwarded: t('Forwarded'),
        pending: t('Pending')
    };
    const severityMap = {
        open: 'warning',
        in_progress: 'info',
        awaiting_customer: 'warning',
        close: 'success',
        forwarded: 'info',
        pending: 'danger'
    };

    return <Tag value={labelMap[value] || t(value) || '-'} severity={severityMap[value] || 'info'} />;
};

const maintenanceStatusBody = (rowData, t = (value) => value) => {
    const value = rowData?.status || 'Scheduled';
    const severityMap = {
        Scheduled: 'info',
        'In Progress': 'warning',
        Completed: 'success',
        Missed: 'danger'
    };

    return <Tag value={t(value)} severity={severityMap[value] || 'info'} />;
};

const CustomerMachineReport = () => {
    const dispatch = useDispatch();
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);
    const [allMachines, setAllMachines] = useState([]);
    const [allComplaints, setAllComplaints] = useState([]);
    const [allMaintenanceTasks, setAllMaintenanceTasks] = useState([]);
    const [allRepairOrders, setAllRepairOrders] = useState([]);
    const [closedRepairOrders, setClosedRepairOrders] = useState([]);
    const [closedRepairOrdersLoading, setClosedRepairOrdersLoading] = useState(false);
    const [selectedMachineIds, setSelectedMachineIds] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [dateFilterMode, setDateFilterMode] = useState('month');
    const [selectedDateRange, setSelectedDateRange] = useState([new Date(), new Date()]);
    const [selectedComplaintDateRange, setSelectedComplaintDateRange] = useState([null, null]);
    const [selectedComplaintCustomers, setSelectedComplaintCustomers] = useState([]);
    const [complaintTableFirst, setComplaintTableFirst] = useState(0);
    const [complaintTableRows, setComplaintTableRows] = useState(10);
    const [detailVisible, setDetailVisible] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [selectedComplaintDetail, setSelectedComplaintDetail] = useState(null);
    const [selectedComplaintRepairOrders, setSelectedComplaintRepairOrders] = useState([]);

    useEffect(() => {
        const loadMachines = async () => {
            const response = await handleGetRequest(endPoints.getallMachine, true, false, dispatch);
            setAllMachines(Array.isArray(response?.data) ? response.data.filter((machine) => machine?.isDeleted !== true) : []);
        };

        const loadComplaints = async () => {
            const response = await handleGetRequest(endPoints.getallComplaints('includeChat=0'), true, false, dispatch);
            setAllComplaints(Array.isArray(response?.data) ? response.data : []);
        };

        const loadMaintenanceTasks = async () => {
            const response = await handleGetRequest(endPoints.getMaintenanceTasks(''), true, false, dispatch);
            const rawTasks = Array.isArray(response?.data?.data)
                ? response.data.data
                : Array.isArray(response?.data)
                    ? response.data
                    : [];
            setAllMaintenanceTasks(rawTasks);
        };

        const loadRepairOrders = async () => {
            const response = await handleGetRequest(endPoints.getRepairOrders(''), true, false, dispatch);
            const rawOrders = Array.isArray(response?.data?.data)
                ? response.data.data
                : Array.isArray(response?.data)
                    ? response.data
                    : [];
            setAllRepairOrders(rawOrders);
        };

        loadMachines();
        loadComplaints();
        loadMaintenanceTasks();
        loadRepairOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const machineOptions = useMemo(
        () =>
            allMachines
                .map((machine) => ({
                    ...machine,
                    label: normalizeMachineLabel(machine, t),
                    value: machine._id
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [allMachines]
    );

    const selectedMachines = useMemo(() => {
        const ids = new Set(selectedMachineIds.map(String));
        return allMachines.filter((machine) => ids.has(String(machine._id)));
    }, [allMachines, selectedMachineIds]);

    const selectedMachineIdSet = useMemo(() => new Set(selectedMachineIds.map(String)), [selectedMachineIds]);
    const machineById = useMemo(
        () => new Map(allMachines.map((machine) => [String(machine?._id), machine])),
        [allMachines]
    );

    // Normalize serial numbers by stripping leading non-digit characters (e.g. "PL7160600520" -> "7160600520")
    // so old and new machine records for the same physical machine can be matched.
    const normalizeSerial = (s) => String(s || '').replace(/^[^0-9]+/, '').trim();

    // Build a set of normalized serial numbers for the selected machines so we can also
    // match complaints linked to old/duplicate machine records that share the
    // same physical machine.
    const selectedNormalizedSerials = useMemo(() => {
        const serials = new Set();
        selectedMachines.forEach((m) => {
            const ns = normalizeSerial(m.serialNumber);
            if (ns) serials.add(ns);
        });
        return serials;
    }, [selectedMachines]);

    const { start: periodStart, end: periodEnd } = useMemo(() => {
        if (dateFilterMode === 'range') {
            return getDayBounds(selectedDateRange?.[0], selectedDateRange?.[1] || selectedDateRange?.[0]);
        }
        return getMonthBounds(selectedMonth);
    }, [dateFilterMode, selectedDateRange, selectedMonth]);

    const selectedPeriodLabel = useMemo(() => {
        if (dateFilterMode === 'range') {
            return `${formatDateOnly(periodStart, preferredLanguage)} - ${formatDateOnly(periodEnd, preferredLanguage)}`;
        }
        return selectedMonth
            ? selectedMonth.toLocaleDateString(preferredLanguage === 'es' ? 'es-PA' : 'en-US', {
                  month: 'long',
                  year: 'numeric'
              })
            : '-';
    }, [dateFilterMode, periodEnd, periodStart, preferredLanguage, selectedMonth]);

    useEffect(() => {
        let cancelled = false;

        const loadClosedRepairOrders = async () => {
            if (!selectedMachineIds.length) {
                setClosedRepairOrders([]);
                setClosedRepairOrdersLoading(false);
                return;
            }

            setClosedRepairOrdersLoading(true);
            try {
                const buildParams = (machineIds = [], machineSerials = []) => {
                    const params = new URLSearchParams({
                        status: 'completed',
                        limit: '5000',
                        page: '1',
                        sortBy: 'closedAt',
                        sortOrder: 'desc',
                        closedFrom: periodStart.toISOString(),
                        closedTo: periodEnd.toISOString()
                    });
                    if (machineIds.length) params.set('machineIds', machineIds.join(','));
                    if (machineSerials.length) params.set('machineSerials', machineSerials.join(','));
                    return params;
                };

                const fetchOrders = async (params) => {
                    const response = await handleGetRequest(
                        endPoints.getRepairOrders(params.toString()),
                        false,
                        false,
                        dispatch
                    );
                    return Array.isArray(response?.data?.data)
                        ? response.data.data
                        : Array.isArray(response?.data)
                            ? response.data
                            : [];
                };

                const allMachinesSelected =
                    allMachines.length > 0 && selectedMachineIds.length === allMachines.length;
                if (allMachinesSelected) {
                    const rawOrders = await fetchOrders(buildParams());
                    if (!cancelled) setClosedRepairOrders(rawOrders);
                    return;
                }

                const batchSize = 40;
                const machineBatches = [];
                for (let index = 0; index < selectedMachines.length; index += batchSize) {
                    machineBatches.push(selectedMachines.slice(index, index + batchSize));
                }

                const ordersById = new Map();
                const concurrency = 4;
                for (let index = 0; index < machineBatches.length; index += concurrency) {
                    const batchGroup = machineBatches.slice(index, index + concurrency);
                    const groupResults = await Promise.all(
                        batchGroup.map((machineBatch) => {
                            const batchMachineIds = machineBatch
                                .map((machine) => machine?._id)
                                .filter(Boolean);
                            const batchSerials = Array.from(
                                new Set(
                                    machineBatch
                                        .map((machine) => normalizeSerial(machine?.serialNumber))
                                        .filter(Boolean)
                                )
                            );
                            return fetchOrders(buildParams(batchMachineIds, batchSerials));
                        })
                    );
                    if (cancelled) return;

                    groupResults.flat().forEach((order) => {
                        if (order?._id) {
                            ordersById.set(String(order._id), order);
                        }
                    });
                }

                if (!cancelled) {
                    setClosedRepairOrders(Array.from(ordersById.values()));
                }
            } finally {
                if (!cancelled) {
                    setClosedRepairOrdersLoading(false);
                }
            }
        };

        loadClosedRepairOrders();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allMachines, periodEnd, periodStart, selectedMachineIds, selectedMachines]);

    const selectedMachineComplaints = useMemo(
        () =>
            allComplaints.filter((complaint) => {
                const cm = complaint?.machine;
                const machineId = cm?._id || complaint?.machine;
                // Direct ID match
                if (selectedMachineIdSet.has(String(machineId))) return true;
                // Fallback: match by normalized serial number (handles duplicate/recreated machines with prefix changes)
                const ns = normalizeSerial(cm?.serialNumber);
                if (ns && selectedNormalizedSerials.has(ns)) return true;
                return false;
            }),
        [allComplaints, selectedMachineIdSet, selectedNormalizedSerials]
    );

    const complaintsThisMonth = useMemo(
        () =>
            selectedMachineComplaints.filter((complaint) => {
                if (!complaint?.createdAt) return false;
                const date = new Date(complaint.createdAt);
                if (Number.isNaN(date.getTime())) return false;
                return date >= periodStart && date <= periodEnd;
            }),
        [selectedMachineComplaints, periodStart, periodEnd]
    );

    const complaintCustomerOptions = useMemo(() => {
        const labels = complaintsThisMonth
            .map((complaint) => getComplaintCustomerLabel(complaint, machineById))
            .filter(Boolean);

        return Array.from(new Set(labels))
            .sort((a, b) => a.localeCompare(b))
            .map((label) => ({ label, value: label }));
    }, [complaintsThisMonth, machineById]);

    const filteredComplaintDetails = useMemo(() => {
        const hasDateFilter = selectedComplaintDateRange?.[0];
        const detailBounds = hasDateFilter
            ? getDayBounds(
                  selectedComplaintDateRange[0],
                  selectedComplaintDateRange[1] || selectedComplaintDateRange[0]
              )
            : null;

        return complaintsThisMonth.filter((complaint) => {
            if (detailBounds) {
                const createdAt = complaint?.createdAt ? new Date(complaint.createdAt) : null;
                if (
                    !createdAt ||
                    Number.isNaN(createdAt.getTime()) ||
                    createdAt < detailBounds.start ||
                    createdAt > detailBounds.end
                ) {
                    return false;
                }
            }

            if (
                selectedComplaintCustomers.length > 0 &&
                !selectedComplaintCustomers.includes(getComplaintCustomerLabel(complaint, machineById))
            ) {
                return false;
            }

            return true;
        });
    }, [
        complaintsThisMonth,
        machineById,
        selectedComplaintCustomers,
        selectedComplaintDateRange
    ]);

    useEffect(() => {
        setComplaintTableFirst(0);
    }, [selectedComplaintCustomers, selectedComplaintDateRange]);

    useEffect(() => {
        setSelectedComplaintDateRange([null, null]);
        setSelectedComplaintCustomers([]);
    }, [periodEnd, periodStart, selectedMachineIds]);

    const closedComplaintsThisMonth = useMemo(
        () =>
            selectedMachineComplaints.filter((complaint) => {
                if (String(complaint?.status || '').toLowerCase() !== 'close' || !complaint?.closedAt) return false;
                const date = new Date(complaint.closedAt);
                if (Number.isNaN(date.getTime())) return false;
                return date >= periodStart && date <= periodEnd;
            }),
        [selectedMachineComplaints, periodStart, periodEnd]
    );

    const currentlyOpenComplaints = useMemo(
        () => selectedMachineComplaints.filter((complaint) => String(complaint?.status || '').toLowerCase() !== 'close'),
        [selectedMachineComplaints]
    );

    const selectedMachineMaintenanceTasks = useMemo(
        () =>
            allMaintenanceTasks.filter((task) =>
                isTaskMachineMatch(task, selectedMachineIdSet, selectedNormalizedSerials, normalizeSerial)
            ),
        [allMaintenanceTasks, selectedMachineIdSet, selectedNormalizedSerials]
    );

    const maintenanceTasksThisMonth = useMemo(
        () =>
            selectedMachineMaintenanceTasks.filter((task) => {
                if (!task?.scheduledDate) return false;
                const date = new Date(task.scheduledDate);
                if (Number.isNaN(date.getTime())) return false;
                return date >= periodStart && date <= periodEnd;
            }),
        [selectedMachineMaintenanceTasks, periodStart, periodEnd]
    );

    const maintenanceSummary = useMemo(() => {
        const completed = maintenanceTasksThisMonth.filter((task) => task?.status === 'Completed').length;
        const missed = maintenanceTasksThisMonth.filter((task) => task?.status === 'Missed').length;
        const scheduled = maintenanceTasksThisMonth.filter((task) => !['Completed', 'Missed'].includes(task?.status)).length;
        const closed = completed + missed;
        const adherenceRate = closed > 0 ? Math.round((completed / closed) * 100) : null;

        return {
            total: maintenanceTasksThisMonth.length,
            completed,
            missed,
            scheduled,
            adherenceRate
        };
    }, [maintenanceTasksThisMonth]);

    const resolvedPercentage = useMemo(() => {
        if (!complaintsThisMonth.length) return null;
        return Math.round((closedComplaintsThisMonth.length / complaintsThisMonth.length) * 100);
    }, [closedComplaintsThisMonth.length, complaintsThisMonth.length]);

    const avgResolutionMinutes = useMemo(() => {
        const closedDurations = closedComplaintsThisMonth
            .map((complaint) => getElapsedMinutes(complaint))
            .filter((value) => Number.isFinite(value));
        if (!closedDurations.length) return null;
        return Math.round(closedDurations.reduce((sum, value) => sum + value, 0) / closedDurations.length);
    }, [closedComplaintsThisMonth]);

    const combinedBreakdownRows = useMemo(() => buildCombinedBreakdownRows(complaintsThisMonth, t), [complaintsThisMonth, t]);

    const repairOrdersByComplaintId = useMemo(() => {
        const grouped = new Map();
        allRepairOrders.forEach((order) => {
            const complaintId = order?.complaint?._id || order?.complaint;
            if (!complaintId) return;
            const key = String(complaintId);
            grouped.set(key, [...(grouped.get(key) || []), order]);
        });
        return grouped;
    }, [allRepairOrders]);

    const getComplaintRepairOrders = (complaint) => repairOrdersByComplaintId.get(String(complaint?._id)) || [];

    const problemThemeRows = useMemo(() => {
        const counts = new Map();
        complaintsThisMonth.forEach((complaint) => {
            const theme = getProblemTheme(complaint, t);
            const current = counts.get(theme) || {
                theme,
                count: 0,
                machineIds: new Set(),
                latestCase: null
            };
            current.count += 1;
            const machineId = complaint?.machine?._id || complaint?.machine;
            if (machineId) current.machineIds.add(String(machineId));
            const createdAt = complaint?.createdAt ? new Date(complaint.createdAt) : null;
            if (createdAt && !Number.isNaN(createdAt.getTime()) && (!current.latestCase || createdAt > current.latestCase)) {
                current.latestCase = createdAt;
            }
            counts.set(theme, current);
        });

        return Array.from(counts.values())
            .map((row) => ({
                theme: row.theme,
                count: row.count,
                percentage: complaintsThisMonth.length ? Math.round((row.count / complaintsThisMonth.length) * 100) : 0,
                machinesAffected: row.machineIds.size,
                latestCase: row.latestCase
            }))
            .sort((a, b) => b.count - a.count || a.theme.localeCompare(b.theme));
    }, [complaintsThisMonth, t]);

    const problemThemeChartData = useMemo(
        () => ({
            labels: problemThemeRows.slice(0, 8).map((row) => row.theme),
            datasets: [
                {
                    label: t('Complaints'),
                    data: problemThemeRows.slice(0, 8).map((row) => row.count),
                    backgroundColor: '#6366f1',
                    borderRadius: 8
                }
            ]
        }),
        [problemThemeRows, t]
    );

    const slaRows = useMemo(() => {
        const order = [t('Closed under 24h'), t('Closed 24-48h'), t('Closed 48h+'), t('Still Open')];
        const counts = new Map(order.map((label) => [label, { label, count: 0 }]));
        complaintsThisMonth.forEach((complaint) => {
            const label = getSlaBand(complaint, t);
            const current = counts.get(label) || { label, count: 0 };
            current.count += 1;
            counts.set(label, current);
        });
        return Array.from(counts.values()).filter((row) => row.count > 0 || order.includes(row.label));
    }, [complaintsThisMonth, t]);

    const slaChartData = useMemo(
        () => ({
            labels: slaRows.map((row) => row.label),
            datasets: [
                {
                    data: slaRows.map((row) => row.count),
                    backgroundColor: ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444']
                }
            ]
        }),
        [slaRows]
    );

    const complaintMatchesMachine = (complaint, machine) => {
        const cm = complaint?.machine;
        const complaintMachineId = cm?._id || complaint?.machine;
        if (String(complaintMachineId) === String(machine._id)) return true;
        const ns1 = normalizeSerial(cm?.serialNumber);
        const ns2 = normalizeSerial(machine.serialNumber);
        if (ns1 && ns2 && ns1 === ns2) return true;
        return false;
    };

    const closedRepairOrdersForSelection = useMemo(
        () =>
            closedRepairOrders
                .filter((order) => {
                    const orderMachine = order?.machine;
                    const machineId = orderMachine?._id || orderMachine;
                    if (selectedMachineIdSet.has(String(machineId))) return true;
                    const normalizedSerial = normalizeSerial(orderMachine?.serialNumber);
                    return normalizedSerial && selectedNormalizedSerials.has(normalizedSerial);
                })
                .sort((a, b) => new Date(b?.closedAt || b?.updatedAt || 0) - new Date(a?.closedAt || a?.updatedAt || 0)),
        [closedRepairOrders, selectedMachineIdSet, selectedNormalizedSerials]
    );

    const closedRepairSummary = useMemo(() => {
        const durations = closedRepairOrdersForSelection
            .map((order) => getRepairOrderElapsedMinutes(order))
            .filter((value) => Number.isFinite(value));
        const under24hCount = closedRepairOrdersForSelection.filter((order) => {
            const minutes = getRepairOrderElapsedMinutes(order);
            return Number.isFinite(minutes) && minutes <= 1440;
        }).length;
        const failureCounts = new Map();
        closedRepairOrdersForSelection.forEach((order) => {
            const label = order?.typeOfFailure || t('Unspecified');
            failureCounts.set(label, (failureCounts.get(label) || 0) + 1);
        });
        const topFailure = Array.from(failureCounts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];

        return {
            total: closedRepairOrdersForSelection.length,
            averageMinutes: durations.length ? Math.round(durations.reduce((sum, value) => sum + value, 0) / durations.length) : null,
            under24hCount,
            topFailureLabel: topFailure?.[0] || '-',
            topFailureCount: topFailure?.[1] || 0
        };
    }, [closedRepairOrdersForSelection, t]);

    const mostAffectedMachine = useMemo(() => {
        const rows = selectedMachines
            .map((machine) => {
                const complaintsCount = complaintsThisMonth.filter((complaint) => complaintMatchesMachine(complaint, machine)).length;
                return {
                    machineName: machine?.machine || 'Unnamed Machine',
                    location: machine?.location || 'No location',
                    complaintsCount
                };
            })
            .filter((row) => row.complaintsCount > 0)
            .sort((a, b) => b.complaintsCount - a.complaintsCount || a.machineName.localeCompare(b.machineName));

        return rows[0] || null;
    }, [selectedMachines, complaintsThisMonth]);

    const statusChartData = useMemo(() => {
        const statusRows = buildBreakdownRows(complaintsThisMonth, (complaint) => {
            const value = String(complaint?.status || '').toLowerCase();
            if (!value) return t('Unknown');
            if (value === 'in_progress') return t('Finance');
            if (value === 'awaiting_customer') return t('Awaiting Customer');
            if (value === 'close') return t('Closed');
            return t(value.charAt(0).toUpperCase() + value.slice(1));
        });

        return {
            labels: statusRows.map((row) => row.label),
            datasets: [
                {
                    data: statusRows.map((row) => row.count),
                    backgroundColor: ['#f59e0b', '#7c3aed', '#fde047', '#10b981', '#3b82f6', '#ef4444']
                }
            ]
        };
    }, [complaintsThisMonth]);

    const machineChartRows = useMemo(() => {
        const rows = selectedMachines
            .map((machine) => {
                const count = complaintsThisMonth.filter((complaint) => complaintMatchesMachine(complaint, machine)).length;
                return {
                    label: machine?.machine || t('Unnamed Machine'),
                    count
                };
            })
            .filter((row) => row.count > 0)
            .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
        return buildTopRows(rows, 8, t('Other'));
    }, [selectedMachines, complaintsThisMonth, t]);

    const machineChartData = useMemo(
        () => ({
            labels: machineChartRows.map((row) => row.label),
            datasets: [
                {
                    label: t('Complaints'),
                    data: machineChartRows.map((row) => row.count),
                    backgroundColor: '#3b82f6',
                    borderRadius: 8
                }
            ]
        }),
        [machineChartRows]
    );

    const weeklyTrendRows = useMemo(() => {
        const periodDays = Math.max(1, Math.ceil((periodEnd.getTime() - periodStart.getTime() + 1) / 86400000));
        const weekCount = Math.max(1, Math.ceil(periodDays / 7));
        const buckets = Array.from({ length: weekCount }, (_, index) => ({
            label: `${t('Week')} ${index + 1}`,
            count: 0
        }));

        complaintsThisMonth.forEach((complaint) => {
            const date = complaint?.createdAt ? new Date(complaint.createdAt) : null;
            if (!date || Number.isNaN(date.getTime())) return;
            const dayOffset = Math.max(0, Math.floor((date.getTime() - periodStart.getTime()) / 86400000));
            const bucketIndex = Math.min(weekCount - 1, Math.floor(dayOffset / 7));
            buckets[bucketIndex].count += 1;
        });

        return buckets;
    }, [complaintsThisMonth, periodEnd, periodStart, t]);

    const weeklyTrendChartData = useMemo(
        () => ({
            labels: weeklyTrendRows.map((row) => row.label),
            datasets: [
                {
                    label: t('Complaints'),
                    data: weeklyTrendRows.map((row) => row.count),
                    backgroundColor: '#2563eb',
                    borderColor: '#2563eb',
                    tension: 0.3,
                    borderRadius: 8
                }
            ]
        }),
        [weeklyTrendRows]
    );

    const locationRows = useMemo(() => {
        const counts = new Map();

        complaintsThisMonth.forEach((complaint) => {
            const location = String(complaint?.machine?.location || 'No location').trim() || 'No location';
            const current = counts.get(location) || { label: location, count: 0, openCount: 0 };
            current.count += 1;
            if (String(complaint?.status || '').toLowerCase() !== 'close') {
                current.openCount += 1;
            }
            counts.set(location, current);
        });

        return Array.from(counts.values()).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    }, [complaintsThisMonth]);

    const topLocationRows = useMemo(() => buildTopRows(locationRows, 8, t('Other')), [locationRows, t]);

    const locationChartData = useMemo(
        () => ({
            labels: topLocationRows.map((row) => row.label),
            datasets: [
                {
                    label: t('Complaints'),
                    data: topLocationRows.map((row) => row.count),
                    backgroundColor: '#0ea5a4',
                    borderRadius: 8
                }
            ]
        }),
        [topLocationRows]
    );

    const complaintRowsPerPageOptions = useMemo(() => {
        const total = filteredComplaintDetails.length;
        return Array.from(new Set([5, 10, 20, total].filter((value) => value > 0))).sort((a, b) => a - b);
    }, [filteredComplaintDetails.length]);

    const openComplaintDetail = async (complaint) => {
        const complaintId = complaint?._id;
        if (!complaintId) return;

        setDetailVisible(true);
        setDetailLoading(true);
        setSelectedComplaintDetail(complaint);
        setSelectedComplaintRepairOrders([]);

        try {
            const [complaintResponse, repairOrderResponse] = await Promise.all([
                handleGetRequest(endPoints.getComplaintById(complaintId), false, false, dispatch),
                handleGetRequest(endPoints.getRepairOrdersByComplaint(complaintId), false, false, dispatch)
            ]);

            const complaintData = complaintResponse?.data?.data || complaintResponse?.data || complaintResponse;
            const repairOrderData = repairOrderResponse?.data?.data || repairOrderResponse?.data || repairOrderResponse;
            setSelectedComplaintDetail(complaintData || complaint);
            setSelectedComplaintRepairOrders(Array.isArray(repairOrderData) ? repairOrderData : []);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeComplaintDetail = () => {
        setDetailVisible(false);
        setSelectedComplaintDetail(null);
        setSelectedComplaintRepairOrders([]);
    };

    const actionBodyTemplate = (rowData) => (
        <Button
            label={t('View')}
            icon="pi pi-eye"
            className="p-button-text p-button-sm"
            onClick={() => openComplaintDetail(rowData)}
        />
    );

    const chatHistoryRows = useMemo(() => {
        if (!Array.isArray(selectedComplaintDetail?.chat)) return [];
        return selectedComplaintDetail.chat
            .filter((entry) => entry && (String(entry?.text || '').trim() || entry?.isImage))
            .map((entry, index) => ({
                ...entry,
                id: entry?._id || `${entry?.createdAt || 'chat'}-${index}`,
                sender: entry?.from || '-',
                message: entry?.isImage ? t('Image or attachment') : String(entry?.text || '').trim(),
                createdAt: entry?.createdAt
            }));
    }, [selectedComplaintDetail, t]);

    const timelineRows = useMemo(() => {
        if (!Array.isArray(selectedComplaintDetail?.logs)) return [];
        return selectedComplaintDetail.logs.map((log, index) => ({
            id: log?._id || `log-${index}`,
            createdAt: log?.createdAt,
            status: log?.status || '-',
            team: log?.team?.name || log?.team || '-',
            updatedBy: `${log?.updatedBy?.firstName || ''} ${log?.updatedBy?.lastName || ''}`.trim() || log?.updatedBy?.email || '-'
        }));
    }, [selectedComplaintDetail]);

    const issuePhotos = useMemo(() => {
        if (!Array.isArray(selectedComplaintDetail?.chat)) return [];
        return selectedComplaintDetail.chat.filter((entry) => entry?.isImage);
    }, [selectedComplaintDetail]);

    const originBodyTemplate = (rowData) => <Tag value={getComplaintOrigin(rowData, t)} severity="info" />;

    const repeatBodyTemplate = (rowData) =>
        isRepeatComplaint(rowData, selectedMachineComplaints, normalizeSerial, 30) ? (
            <Tag value={t('Repeat')} severity="warning" />
        ) : (
            <Tag value={t('No')} severity="success" />
        );

    const repairOrderBodyTemplate = (rowData) => {
        const summary = getRepairOrderSummary(getComplaintRepairOrders(rowData), t);
        return (
            <div>
                <div className="font-semibold">
                    {summary.link ? <a href={summary.link}>{summary.code}</a> : summary.code}
                </div>
                <small className="text-color-secondary">{summary.status}</small>
            </div>
        );
    };

    const slaBodyTemplate = (rowData) => <Tag value={getSlaBand(rowData, t)} severity={String(rowData?.status || '').toLowerCase() === 'close' ? 'success' : 'warning'} />;

    const exportComplaintReport = () => {
        const summaryRows = [
            {
                [t('Report Type')]: t('Executive Summary'),
                [t('Metric')]: t('Complaints Opened'),
                [t('Value')]: complaintsThisMonth.length
            },
            {
                [t('Report Type')]: t('Executive Summary'),
                [t('Metric')]: t('Resolved %'),
                [t('Value')]: resolvedPercentage !== null ? `${resolvedPercentage}%` : '-'
            },
            {
                [t('Report Type')]: t('Executive Summary'),
                [t('Metric')]: t('Average Resolution Time'),
                [t('Value')]: avgResolutionMinutes !== null ? formatTimeFromMinutes(avgResolutionMinutes, preferredLanguage) : '-'
            },
            {
                [t('Report Type')]: t('Executive Summary'),
                [t('Metric')]: t('Currently Open'),
                [t('Value')]: currentlyOpenComplaints.length
            },
            {
                [t('Report Type')]: t('Executive Summary'),
                [t('Metric')]: t('Preventive Maintenance Adherence'),
                [t('Value')]: maintenanceSummary.adherenceRate !== null ? `${maintenanceSummary.adherenceRate}%` : '-'
            }
        ];

        const themeRows = problemThemeRows.map((row) => ({
            [t('Report Type')]: t('Top Problem Theme'),
            [t('Problem')]: row.theme,
            [t('Count')]: row.count,
            [t('% of Total')]: `${row.percentage}%`,
            [t('Machines Affected')]: row.machinesAffected,
            [t('Latest Case')]: formatDateTime(row.latestCase, preferredLanguage)
        }));

        const slaExportRows = slaRows.map((row) => ({
            [t('Report Type')]: t('SLA / Resolution Band'),
            [t('SLA Band')]: row.label,
            [t('Count')]: row.count,
            [t('% of Total')]: complaintsThisMonth.length ? `${Math.round((row.count / complaintsThisMonth.length) * 100)}%` : '0%'
        }));

        const complaintRows = complaintsThisMonth.map((complaint) => {
            const repairSummary = getRepairOrderSummary(getComplaintRepairOrders(complaint), t);
            return ({
            [t('Report Type')]: t('Complaint'),
            [t('Created At')]: formatDateTime(complaint.createdAt, preferredLanguage),
            [t('Closed At')]: formatDateTime(complaint.closedAt, preferredLanguage),
            [t('Machine')]: complaint?.machine?.machine || '-',
            [t('Location')]: complaint?.machine?.location || '-',
            [t('Route')]: complaint?.machine?.route || '-',
            [t('Origin Type')]: getComplaintOrigin(complaint, t),
            [t('Status')]: complaint?.status || '-',
            [t('SLA Band')]: getSlaBand(complaint, t),
            [t('Repeat Complaint')]: isRepeatComplaint(complaint, selectedMachineComplaints, normalizeSerial, 30) ? t('Yes') : t('No'),
            [t('Problem Theme')]: getProblemTheme(complaint, t),
            [t('Issue Summary')]: getIssueSummary(complaint),
            [t('Level 1')]: getLevelOneLabel(complaint),
            [t('Level 2')]: getLevelTwoLabel(complaint),
            [t('Level 3')]: getLevelThreeLabel(complaint),
            [t('Repair Order')]: repairSummary.code,
            [t('Repair Status')]: repairSummary.status,
            [t('Technician')]: repairSummary.technician,
            [t('Failure Type')]: repairSummary.failureType,
            [t('Failure Category')]: repairSummary.failureCategory,
            [t('Repair Time')]: repairSummary.timeToClose,
            [t('Actions Taken')]: repairSummary.actionsTaken,
            [t('Preventive Maintenance Tasks')]: maintenanceSummary.total,
            [t('Preventive Maintenance Completed')]: maintenanceSummary.completed,
            [t('Preventive Maintenance Missed')]: maintenanceSummary.missed,
            [t('Preventive Maintenance Adherence')]: maintenanceSummary.adherenceRate !== null ? `${maintenanceSummary.adherenceRate}%` : '-',
            [t('WhatsApp Number')]: complaint?.whatsAppNumber || '-'
            });
        });

        const maintenanceRows = maintenanceTasksThisMonth.map((task) => ({
            [t('Report Type')]: t('Preventive Maintenance'),
            [t('Created At')]: '-',
            [t('Closed At')]: '-',
            [t('Machine')]: getMaintenanceMachineLabel(task, t),
            [t('Location')]: Array.isArray(task?.machines) ? task.machines.map((machine) => machine?.location).filter(Boolean).join(', ') || '-' : '-',
            [t('Route')]: task?.route || '-',
            [t('Status')]: task?.status || '-',
            [t('Issue Summary')]: task?.completionNotes || task?.comments || '-',
            [t('Level 1')]: '-',
            [t('Level 2')]: '-',
            [t('Level 3')]: '-',
            [t('Preventive Maintenance Tasks')]: maintenanceSummary.total,
            [t('Preventive Maintenance Completed')]: maintenanceSummary.completed,
            [t('Preventive Maintenance Missed')]: maintenanceSummary.missed,
            [t('Preventive Maintenance Adherence')]: maintenanceSummary.adherenceRate !== null ? `${maintenanceSummary.adherenceRate}%` : '-',
            [t('WhatsApp Number')]: '-'
        }));

        exportToExcel(
            [...summaryRows, ...themeRows, ...slaExportRows, ...complaintRows, ...maintenanceRows],
            `Customer_Complaint_Report_${periodStart.toISOString().slice(0, 10)}_${periodEnd.toISOString().slice(0, 10)}`
        );
    };

    const hasSelection = selectedMachineIds.length > 0;

    return (
        <div className="p-4">
            <div className="mb-4 flex justify-content-between align-items-start gap-3 flex-wrap">
                <div>
                    <h2>{t('Customer Complaint Reports')}</h2>
                    <p className="text-color-secondary">{t('Monthly customer scorecard for selected machines, focused on complaint volume, resolution, and recurring problem areas.')}</p>
                </div>
                <Button
                    label={t('Export to Excel')}
                    icon="pi pi-file-excel"
                    className="p-button-success"
                    onClick={exportComplaintReport}
                    disabled={!hasSelection || (complaintsThisMonth.length === 0 && maintenanceTasksThisMonth.length === 0)}
                />
            </div>

            <div className="card mb-4">
                <div className="grid">
                    <div className="col-12 md:col-8">
                        <label className="block mb-2">{t('Machines')}</label>
                        <MultiSelect
                            value={selectedMachineIds}
                            options={machineOptions}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => setSelectedMachineIds(e.value || [])}
                            placeholder={t('Type a machine, location, serial, device ID, or UUID')}
                            className="w-full"
                            display="chip"
                            filter
                            filterBy="label"
                            maxSelectedLabels={3}
                        />
                    </div>
                    <div className="col-12 md:col-4">
                        <label className="block mb-2">{t('Period')}</label>
                        <div className="flex gap-2 mb-2">
                            <Button
                                type="button"
                                label={t('Month')}
                                className={dateFilterMode === 'month' ? 'p-button-primary p-button-sm' : 'p-button-outlined p-button-sm'}
                                onClick={() => setDateFilterMode('month')}
                            />
                            <Button
                                type="button"
                                label={t('Date Range')}
                                className={dateFilterMode === 'range' ? 'p-button-primary p-button-sm' : 'p-button-outlined p-button-sm'}
                                onClick={() => setDateFilterMode('range')}
                            />
                        </div>
                        {dateFilterMode === 'range' ? (
                            <Calendar
                                value={selectedDateRange}
                                onChange={(e) => setSelectedDateRange(Array.isArray(e.value) ? e.value : [new Date(), new Date()])}
                                selectionMode="range"
                                readOnlyInput
                                placeholder={t('Select date range')}
                                showIcon
                                className="w-full"
                            />
                        ) : (
                            <Calendar
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.value || new Date())}
                                view="month"
                                dateFormat="MM yy"
                                placeholder={t('Select month')}
                                showIcon
                                className="w-full"
                            />
                        )}
                    </div>
                </div>
                <div className="flex gap-2 mt-3">
                    <Button
                        label={t('Clear Selection')}
                        icon="pi pi-times"
                        className="p-button-secondary"
                        onClick={() => {
                            setSelectedMachineIds([]);
                            setSelectedMonth(new Date());
                            setSelectedDateRange([new Date(), new Date()]);
                            setDateFilterMode('month');
                        }}
                    />
                </div>
            </div>

            {!hasSelection ? (
                <Card>
                    <div className="text-center py-6">
                        <h4 className="mb-2">{t('Select machines to build the report')}</h4>
                        <p className="text-color-secondary m-0">{t('You can type names like Metro, location text, serial numbers, device IDs, or UUIDs.')}</p>
                    </div>
                </Card>
            ) : (
                <>
                    <div className="grid mb-4">
                        <div className="col-12 md:col-6 xl:col-2">
                            <Card>
                                <div className="text-center">
                                    <div className="text-2xl font-bold" style={{ color: '#f59e0b' }}>{complaintsThisMonth.length}</div>
                                    <div className="text-color-secondary">{t('Complaints Opened')}</div>
                                </div>
                            </Card>
                        </div>
                        <div className="col-12 md:col-6 xl:col-2">
                            <Card>
                                <div className="text-center">
                                    <div className="text-2xl font-bold" style={{ color: '#16a34a' }}>{resolvedPercentage !== null ? `${resolvedPercentage}%` : '-'}</div>
                                    <div className="text-color-secondary">{t('Resolved %')}</div>
                                </div>
                            </Card>
                        </div>
                        <div className="col-12 md:col-6 xl:col-2">
                            <Card>
                                <div className="text-center">
                                    <div className="text-2xl font-bold" style={{ color: '#7c3aed' }}>{avgResolutionMinutes !== null ? formatTimeFromMinutes(avgResolutionMinutes, preferredLanguage) : '-'}</div>
                                    <div className="text-color-secondary">{t('Average Resolution Time')}</div>
                                </div>
                            </Card>
                        </div>
                        <div className="col-12 md:col-6 xl:col-2">
                            <Card>
                                <div className="text-center">
                                    <div className="text-2xl font-bold" style={{ color: '#ef4444' }}>{currentlyOpenComplaints.length}</div>
                                    <div className="text-color-secondary">{t('Currently Open')}</div>
                                </div>
                            </Card>
                        </div>
                        <div className="col-12 md:col-12 xl:col-4">
                            <Card>
                                <div>
                                    <div className="text-color-secondary mb-2">{t('Most Affected Machine')}</div>
                                    {mostAffectedMachine ? (
                                        <>
                                            <div className="text-xl font-bold text-900 mb-1">{mostAffectedMachine.machineName}</div>
                                            <div className="text-color-secondary mb-2">{mostAffectedMachine.location}</div>
                                            <Tag value={`${mostAffectedMachine.complaintsCount} ${t(mostAffectedMachine.complaintsCount === 1 ? 'complaint' : 'complaints')}`} severity="warning" />
                                        </>
                                    ) : (
                                        <div className="text-color-secondary">{t('No complaints for the selected month.')}</div>
                                    )}
                                </div>
                            </Card>
                        </div>
                    </div>

                    <div className="grid mb-4">
                        <div className="col-12 lg:col-4">
                            <div className="card h-full">
                                <h4 className="mb-3">{t('Complaints by Status')}</h4>
                                {complaintsThisMonth.length ? (
                                    <div style={{ height: '320px' }}>
                                        <Chart type="doughnut" data={statusChartData} options={buildDoughnutOptions(t('Complaints by Status'))} plugins={[doughnutValuePlugin]} />
                                    </div>
                                ) : (
                                    <div className="text-color-secondary py-6 text-center">{t('No complaint status data for the selected month.')}</div>
                                )}
                            </div>
                        </div>
                        <div className="col-12 lg:col-4">
                            <div className="card h-full">
                                <h4 className="mb-3">{t('Complaints by Machine')}</h4>
                                {machineChartRows.length ? (
                                    <div style={{ height: '320px' }}>
                                        <Chart type="bar" data={machineChartData} options={buildChartOptions(t('Complaints by Machine'), 'y')} />
                                    </div>
                                ) : (
                                    <div className="text-color-secondary py-6 text-center">{t('No machine complaint data for the selected month.')}</div>
                                )}
                            </div>
                        </div>
                        <div className="col-12 lg:col-4">
                            <div className="card h-full">
                                <h4 className="mb-3">{t('Complaint Trend by Week')}</h4>
                                {complaintsThisMonth.length ? (
                                    <div style={{ height: '320px' }}>
                                        <Chart type="bar" data={weeklyTrendChartData} options={buildChartOptions(t('Complaint Trend by Week'))} />
                                    </div>
                                ) : (
                                    <div className="text-color-secondary py-6 text-center">{t('No weekly complaint data for the selected month.')}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="grid mb-4">
                        <div className="col-12 lg:col-6">
                            <div className="card h-full">
                                <h4 className="mb-3">{t('Top Affected Locations')}</h4>
                                {topLocationRows.length ? (
                                    <div style={{ height: '320px' }}>
                                        <Chart type="bar" data={locationChartData} options={buildChartOptions(t('Top Affected Locations'), 'y')} />
                                    </div>
                                ) : (
                                    <div className="text-color-secondary py-6 text-center">{t('No location complaint data for the selected month.')}</div>
                                )}
                            </div>
                        </div>
                        <div className="col-12 lg:col-6">
                            <div className="card h-full">
                                <h4 className="mb-3">{t('Location Summary')}</h4>
                                <DataTable value={locationRows} rows={8} emptyMessage={t('No location complaint data for the selected month.')} responsiveLayout="scroll">
                                    <Column field="label" header={t('Location')} style={{ minWidth: '16rem' }} />
                                    <Column field="count" header={t('Complaints')} />
                                    <Column field="openCount" header={t('Open')} />
                                </DataTable>
                            </div>
                        </div>
                    </div>

                    <div className="grid mb-4">
                        <div className="col-12 lg:col-7">
                            <div className="card h-full">
                                <h4 className="mb-3">{t('Top Problem Themes')}</h4>
                                {problemThemeRows.length ? (
                                    <div style={{ height: '320px' }}>
                                        <Chart type="bar" data={problemThemeChartData} options={buildChartOptions(t('Top Problem Themes'), 'y')} />
                                    </div>
                                ) : (
                                    <div className="text-color-secondary py-6 text-center">{t('No problem theme data for the selected month.')}</div>
                                )}
                            </div>
                        </div>
                        <div className="col-12 lg:col-5">
                            <div className="card h-full">
                                <h4 className="mb-3">{t('SLA / Resolution Bands')}</h4>
                                {complaintsThisMonth.length ? (
                                    <div style={{ height: '320px' }}>
                                        <Chart type="doughnut" data={slaChartData} options={buildDoughnutOptions(t('SLA / Resolution Bands'))} plugins={[doughnutValuePlugin]} />
                                    </div>
                                ) : (
                                    <div className="text-color-secondary py-6 text-center">{t('No SLA data for the selected month.')}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="grid mb-4">
                        <div className="col-12">
                            <div className="card h-full">
                                <h4 className="mb-3">{t('Problem Theme Summary')}</h4>
                                <DataTable value={problemThemeRows} rows={8} emptyMessage={t('No problem theme data for the selected month.')} responsiveLayout="scroll">
                                    <Column field="theme" header={t('Problem')} style={{ minWidth: '14rem' }} />
                                    <Column field="count" header={t('Count')} />
                                    <Column field="percentage" header={t('% of Total')} body={(rowData) => `${rowData.percentage}%`} />
                                    <Column field="machinesAffected" header={t('Machines Affected')} />
                                    <Column field="latestCase" header={t('Latest Case')} body={(rowData) => formatDateTime(rowData.latestCase, preferredLanguage)} style={{ minWidth: '12rem' }} />
                                </DataTable>
                            </div>
                        </div>
                    </div>

                    <div className="grid mb-4">
                        <div className="col-12">
                            <div className="card h-full">
                                <h4 className="mb-3">{t('Chat Selection Breakdown')}</h4>
                                <DataTable value={combinedBreakdownRows} rows={8} emptyMessage={t('No chat selection data for the selected month.')} responsiveLayout="scroll">
                                    <Column field="level1" header={t('Level 1')} style={{ minWidth: '14rem' }} />
                                    <Column field="level2" header={t('Level 2')} style={{ minWidth: '14rem' }} />
                                    <Column field="level3" header={t('Level 3')} style={{ minWidth: '14rem' }} />
                                    <Column field="count" header={t('Count')} />
                                </DataTable>
                            </div>
                        </div>
                    </div>

                    <div className="grid mb-4">
                        <div className="col-12">
                            <div className="card h-full">
                                <div className="flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
                                    <div>
                                        <h4 className="mb-1">{t('Preventive Maintenance')}</h4>
                                        <div className="text-color-secondary">{t('Scheduled preventive maintenance for the selected machines and month.')}</div>
                                    </div>
                                    <div className="flex gap-2 flex-wrap">
                                        <Tag value={`${maintenanceSummary.total} ${t('tasks')}`} severity="info" />
                                        <Tag value={`${maintenanceSummary.completed} ${t('completed')}`} severity="success" />
                                        <Tag value={`${maintenanceSummary.missed} ${t('missed')}`} severity="danger" />
                                        <Tag value={`${maintenanceSummary.adherenceRate !== null ? maintenanceSummary.adherenceRate : '-'}% ${t('adherence')}`} severity="warning" />
                                    </div>
                                </div>
                                <DataTable value={maintenanceTasksThisMonth} rows={8} paginator={maintenanceTasksThisMonth.length > 8} emptyMessage={t('No preventive maintenance tasks for the selected month.')} responsiveLayout="scroll">
                                    <Column field="scheduledDate" header={t('Scheduled Date')} body={(rowData) => formatDateOnly(rowData.scheduledDate, preferredLanguage)} style={{ minWidth: '10rem' }} />
                                    <Column field="taskType" header={t('Task Type')} style={{ minWidth: '12rem' }} />
                                    <Column header={t('Machine')} body={(rowData) => getMaintenanceMachineLabel(rowData, t)} style={{ minWidth: '18rem' }} />
                                    <Column field="assignedTo" header={t('Technician')} body={(rowData) => `${rowData?.assignedTo?.firstName || ''} ${rowData?.assignedTo?.lastName || ''}`.trim() || '-'} style={{ minWidth: '12rem' }} />
                                    <Column header={t('Status')} body={(rowData) => maintenanceStatusBody(rowData, t)} style={{ minWidth: '10rem' }} />
                                    <Column field="comments" header={t('Notes')} body={(rowData) => truncateText(rowData?.completionNotes || rowData?.comments || '-', 120)} style={{ minWidth: '16rem' }} />
                                </DataTable>
                            </div>
                        </div>
                    </div>

                    <div className="grid mb-4">
                        <div className="col-12">
                            <div className="card h-full">
                                <div className="flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
                                    <div>
                                        <h4 className="mb-1">{t('Closed Repair Orders')}</h4>
                                        <div className="text-color-secondary">
                                            {t('Closed repair work for the selected machines and period.')} {selectedPeriodLabel}
                                        </div>
                                    </div>
                                    <div className="flex gap-2 flex-wrap">
                                        <Tag value={`${closedRepairSummary.total} ${t('closed orders')}`} severity="success" />
                                        <Tag value={`${closedRepairSummary.averageMinutes !== null ? formatTimeFromMinutes(closedRepairSummary.averageMinutes, preferredLanguage) : '-'} ${t('average close time')}`} severity="info" />
                                        <Tag value={`${closedRepairSummary.under24hCount} ${t('closed under 24h')}`} severity="warning" />
                                        <Tag value={`${t('Top failure')}: ${closedRepairSummary.topFailureLabel !== '-' ? t(closedRepairSummary.topFailureLabel) : '-'}`} severity="danger" />
                                    </div>
                                </div>
                                <DataTable
                                    value={closedRepairOrdersForSelection}
                                    rows={8}
                                    paginator={closedRepairOrdersForSelection.length > 8}
                                    loading={closedRepairOrdersLoading}
                                    emptyMessage={t('No closed repair orders were recorded for the selected machines and period.')}
                                    responsiveLayout="scroll"
                                >
                                    <Column field="closedAt" header={t('Close Date')} body={(rowData) => formatDateOnly(rowData.closedAt, preferredLanguage)} style={{ minWidth: '10rem' }} />
                                    <Column header={t('Machine')} body={(rowData) => normalizeMachineLabel(rowData.machine || {}, t)} style={{ minWidth: '18rem' }} />
                                    <Column field="machine.location" header={t('Location')} body={(rowData) => rowData?.machine?.location || '-'} style={{ minWidth: '14rem' }} />
                                    <Column header={t('Technician')} body={(rowData) => getTechnicianName(rowData)} style={{ minWidth: '12rem' }} />
                                    <Column field="typeOfFailure" header={t('Failure Type')} body={(rowData) => t(rowData?.typeOfFailure || '-')} style={{ minWidth: '12rem' }} />
                                    <Column field="actionsTaken" header={t('Action Performed')} body={(rowData) => <span title={rowData?.actionsTaken || '-'}>{truncateText(rowData?.actionsTaken || '-', 120)}</span>} style={{ minWidth: '18rem' }} />
                                    <Column header={t('Close Time')} body={(rowData) => {
                                        const minutes = getRepairOrderElapsedMinutes(rowData);
                                        return Number.isFinite(minutes) ? formatTimeFromMinutes(minutes, preferredLanguage) : '-';
                                    }} style={{ minWidth: '10rem' }} />
                                    <Column field="finalStatus" header={t('Result')} body={(rowData) => getRepairResult(rowData, t)} style={{ minWidth: '14rem' }} />
                                </DataTable>
                            </div>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12">
                            <div className="card h-full">
                                <h4 className="mb-3">{t('Complaint Detail')}</h4>
                                <div className="surface-50 border-round p-3 mb-3">
                                    <div className="grid align-items-end">
                                        <div className="col-12 md:col-5">
                                            <label className="block mb-2">{t('Complaint Date')}</label>
                                            <Calendar
                                                value={selectedComplaintDateRange}
                                                onChange={(e) =>
                                                    setSelectedComplaintDateRange(
                                                        Array.isArray(e.value) ? e.value : [null, null]
                                                    )
                                                }
                                                selectionMode="range"
                                                readOnlyInput
                                                minDate={periodStart}
                                                maxDate={periodEnd}
                                                placeholder={t('Select date range')}
                                                showIcon
                                                className="w-full"
                                            />
                                        </div>
                                        <div className="col-12 md:col-5">
                                            <label className="block mb-2">{t('Customer')}</label>
                                            <MultiSelect
                                                value={selectedComplaintCustomers}
                                                options={complaintCustomerOptions}
                                                onChange={(e) => setSelectedComplaintCustomers(e.value || [])}
                                                placeholder={t('All Customers')}
                                                emptyMessage={t('No customers available')}
                                                optionLabel="label"
                                                optionValue="value"
                                                display="chip"
                                                maxSelectedLabels={3}
                                                filter
                                                showClear
                                                className="w-full"
                                            />
                                        </div>
                                        <div className="col-12 md:col-2">
                                            <Button
                                                type="button"
                                                label={t('Clear Filters')}
                                                icon="pi pi-filter-slash"
                                                className="p-button-outlined w-full"
                                                disabled={
                                                    !selectedComplaintDateRange?.[0] &&
                                                    selectedComplaintCustomers.length === 0
                                                }
                                                onClick={() => {
                                                    setSelectedComplaintDateRange([null, null]);
                                                    setSelectedComplaintCustomers([]);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <DataTable
                                    value={filteredComplaintDetails}
                                    paginator
                                    first={complaintTableFirst}
                                    onPage={(event) => {
                                        setComplaintTableFirst(event.first);
                                        setComplaintTableRows(event.rows);
                                    }}
                                    rows={complaintTableRows}
                                    rowsPerPageOptions={complaintRowsPerPageOptions}
                                    emptyMessage={t('No complaints match the selected filters.')}
                                    responsiveLayout="scroll"
                                >
                                    <Column field="createdAt" header={t('Created At')} body={(rowData) => formatDateTime(rowData.createdAt, preferredLanguage)} />
                                    <Column field="closedAt" header={t('Closed At')} body={(rowData) => formatDateTime(rowData.closedAt, preferredLanguage)} />
                                    <Column header={t('Time Elapsed')} body={complaintElapsedBody} style={{ minWidth: '10rem' }} />
                                    <Column
                                        header={t('Customer')}
                                        body={(rowData) => getComplaintCustomerLabel(rowData, machineById) || '-'}
                                        style={{ minWidth: '14rem' }}
                                    />
                                    <Column header={t('Machine')} body={(rowData) => normalizeMachineLabel(rowData.machine || {}, t)} style={{ minWidth: '16rem' }} />
                                    <Column header={t('Origin Type')} body={originBodyTemplate} style={{ minWidth: '12rem' }} />
                                    <Column header={t('Status')} body={(rowData) => complaintStatusBody(rowData, t)} />
                                    <Column header={t('SLA Band')} body={slaBodyTemplate} style={{ minWidth: '10rem' }} />
                                    <Column header={t('Repeat Complaint')} body={repeatBodyTemplate} style={{ minWidth: '10rem' }} />
                                    <Column header={t('Repair Order')} body={repairOrderBodyTemplate} style={{ minWidth: '10rem' }} />
                                    <Column header={t('Issue Summary')} body={(rowData) => <span title={getIssueSummary(rowData)}>{truncateText(getIssueSummary(rowData))}</span>} style={{ minWidth: '16rem' }} />
                                    <Column header={t('WhatsApp')} body={(rowData) => maskWhatsAppNumber(rowData?.whatsAppNumber)} />
                                    <Column header={t('Actions')} body={actionBodyTemplate} style={{ minWidth: '8rem' }} />
                                </DataTable>
                            </div>
                        </div>
                    </div>

                    <Dialog
                        header={t('Complaint Detail')}
                        visible={detailVisible}
                        style={{ width: 'min(1100px, 96vw)' }}
                        modal
                        onHide={closeComplaintDetail}
                    >
                        {detailLoading && (
                            <div className="flex align-items-center gap-2 mb-3 text-color-secondary">
                                <i className="pi pi-spin pi-spinner" />
                                <span>{t('Loading complaint details...')}</span>
                            </div>
                        )}

                        {selectedComplaintDetail && (
                            <div className="grid">
                                <div className="col-12 lg:col-6">
                                    <div className="surface-50 border-round p-3 h-full">
                                        <h5 className="mt-0 mb-3">{t('Summary')}</h5>
                                        <div className="mb-3">
                                            <div className="text-color-secondary text-sm mb-1">{t('Issue Summary')}</div>
                                            <div className="font-semibold white-space-pre-line">{getIssueSummary(selectedComplaintDetail)}</div>
                                        </div>
                                        <div className="grid">
                                            <div className="col-12 md:col-6">
                                                <div className="text-color-secondary text-sm">{t('Created At')}</div>
                                                <div>{formatDateTime(selectedComplaintDetail.createdAt, preferredLanguage)}</div>
                                            </div>
                                            <div className="col-12 md:col-6">
                                                <div className="text-color-secondary text-sm">{t('Closed At')}</div>
                                                <div>{formatDateTime(selectedComplaintDetail.closedAt, preferredLanguage)}</div>
                                            </div>
                                            <div className="col-12 md:col-6">
                                                <div className="text-color-secondary text-sm">{t('Status')}</div>
                                                <div>{complaintStatusBody(selectedComplaintDetail, t)}</div>
                                            </div>
                                            <div className="col-12 md:col-6">
                                                <div className="text-color-secondary text-sm">{t('WhatsApp')}</div>
                                                <div>{maskWhatsAppNumber(selectedComplaintDetail?.whatsAppNumber)}</div>
                                            </div>
                                            <div className="col-12 md:col-6">
                                                <div className="text-color-secondary text-sm">{t('Origin Type')}</div>
                                                <div>{getComplaintOrigin(selectedComplaintDetail, t)}</div>
                                            </div>
                                            <div className="col-12 md:col-6">
                                                <div className="text-color-secondary text-sm">{t('Problem Theme')}</div>
                                                <div>{getProblemTheme(selectedComplaintDetail, t)}</div>
                                            </div>
                                            <div className="col-12 md:col-6">
                                                <div className="text-color-secondary text-sm">{t('SLA Band')}</div>
                                                <div>{getSlaBand(selectedComplaintDetail, t)}</div>
                                            </div>
                                            <div className="col-12 md:col-6">
                                                <div className="text-color-secondary text-sm">{t('Repeat Complaint')}</div>
                                                <div>{isRepeatComplaint(selectedComplaintDetail, selectedMachineComplaints, normalizeSerial, 30) ? t('Yes') : t('No')}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 lg:col-6">
                                    <div className="surface-50 border-round p-3 h-full">
                                        <h5 className="mt-0 mb-3">{t('Machine and Selection')}</h5>
                                        <div className="mb-3">
                                            <div className="text-color-secondary text-sm">{t('Machine')}</div>
                                            <div className="font-semibold">{normalizeMachineLabel(selectedComplaintDetail.machine || {}, t)}</div>
                                        </div>
                                        <div className="grid">
                                            <div className="col-12">
                                                <div className="text-color-secondary text-sm">{t('Level 1')}</div>
                                                <div>{getLevelOneLabel(selectedComplaintDetail)}</div>
                                            </div>
                                            <div className="col-12">
                                                <div className="text-color-secondary text-sm">{t('Level 2')}</div>
                                                <div>{getLevelTwoLabel(selectedComplaintDetail)}</div>
                                            </div>
                                            <div className="col-12">
                                                <div className="text-color-secondary text-sm">{t('Level 3')}</div>
                                                <div>{getLevelThreeLabel(selectedComplaintDetail)}</div>
                                            </div>
                                            <div className="col-12">
                                                <div className="text-color-secondary text-sm">{t('Photos')}</div>
                                                <div>{issuePhotos.length ? `${issuePhotos.length} ${t(issuePhotos.length === 1 ? 'photo' : 'photos')}` : '-'}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="surface-50 border-round p-3">
                                        <h5 className="mt-0 mb-3">{t('Chat History')}</h5>
                                        <DataTable value={chatHistoryRows} rows={8} paginator={chatHistoryRows.length > 8} emptyMessage={t('No chat history was saved for this older ticket.')} responsiveLayout="scroll">
                                            <Column field="createdAt" header={t('Created At')} body={(rowData) => formatDateTime(rowData.createdAt, preferredLanguage)} style={{ minWidth: '12rem' }} />
                                            <Column field="sender" header={t('Sender')} style={{ minWidth: '8rem' }} />
                                            <Column field="message" header={t('Message')} body={(rowData) => <span className="white-space-pre-line">{rowData.message}</span>} style={{ minWidth: '20rem' }} />
                                        </DataTable>
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="surface-50 border-round p-3">
                                        <h5 className="mt-0 mb-3">{t('Timeline / Status Logs')}</h5>
                                        <DataTable value={timelineRows} rows={6} paginator={timelineRows.length > 6} emptyMessage={t('No status logs found for this complaint.')} responsiveLayout="scroll">
                                            <Column field="createdAt" header={t('Created At')} body={(rowData) => formatDateTime(rowData.createdAt, preferredLanguage)} style={{ minWidth: '12rem' }} />
                                            <Column field="status" header={t('Status')} body={(rowData) => t(rowData.status || '-')} />
                                            <Column field="team" header={t('Team')} />
                                            <Column field="updatedBy" header={t('Updated By')} />
                                        </DataTable>
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="surface-50 border-round p-3">
                                        <h5 className="mt-0 mb-3">{t('Repair Orders')}</h5>
                                        <DataTable value={selectedComplaintRepairOrders} rows={5} emptyMessage={t('No repair orders linked to this complaint.')} responsiveLayout="scroll">
                                            <Column header={t('Order')} body={(rowData) => (getRepairOrderLink(rowData) ? <a href={getRepairOrderLink(rowData)}>{getRepairOrderCode(rowData)}</a> : '-')} />
                                            <Column field="status" header={t('Status')} body={(rowData) => t(rowData?.status || '-')} />
                                            <Column header={t('Technician')} body={(rowData) => `${rowData?.technician?.firstName || ''} ${rowData?.technician?.lastName || ''}`.trim() || '-'} />
                                            <Column field="typeOfFailure" header={t('Failure Type')} body={(rowData) => rowData?.typeOfFailure || '-'} />
                                            <Column field="failureCategory" header={t('Failure Category')} body={(rowData) => rowData?.failureCategory || '-'} />
                                            <Column field="timeConsumedMinutes" header={t('Repair Time')} body={(rowData) => Number.isFinite(rowData?.timeConsumedMinutes) ? formatTimeFromMinutes(rowData.timeConsumedMinutes, preferredLanguage) : '-'} />
                                            <Column field="actionsTaken" header={t('Actions Taken')} body={(rowData) => <span title={rowData?.actionsTaken || '-'}>{truncateText(rowData?.actionsTaken || '-', 120)}</span>} style={{ minWidth: '14rem' }} />
                                            <Column field="createdAt" header={t('Created At')} body={(rowData) => formatDateTime(rowData.createdAt, preferredLanguage)} />
                                        </DataTable>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Dialog>
                </>
            )}
        </div>
    );
};

export default CustomerMachineReport;
